import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Home, ClipboardList, ShieldAlert, Wallet, User } from "lucide-react";

/* GEN-DO wordmark for dark headers */
export function Logo({ size = "md", light = true }) {
  const s = size === "lg" ? { f: 34, sub: 10 } : size === "sm" ? { f: 18, sub: 6 } : { f: 22, sub: 7 };
  return (
    <div className="inline-flex flex-col items-start leading-none">
      <div style={{ fontSize: s.f, fontWeight: 900, letterSpacing: "0.5px" }}>
        <span style={{ color: light ? "#fff" : "#2b3f96" }}>GEN</span>
        <span style={{ color: "#f5911e" }}>-DO</span>
      </div>
      <div
        style={{
          background: "#f5911e",
          color: "#fff",
          fontSize: s.sub,
          fontWeight: 800,
          letterSpacing: "2px",
          padding: "1px 6px",
          borderRadius: 3,
          marginTop: 3,
        }}
      >
        GROWING SUCCESS!
      </div>
    </div>
  );
}

/* iOS-style status bar */
export function StatusBar({ dark = false }) {
  const c = dark ? "#1f2a52" : "#fff";
  return (
    <div className="gd-statusbar" style={{ color: c }}>
      <span>9:41</span>
      <div className="flex items-center gap-1.5">
        <svg width="17" height="11" viewBox="0 0 17 11" fill={c}>
          <rect x="0" y="7" width="3" height="4" rx="1" /><rect x="4.5" y="5" width="3" height="6" rx="1" />
          <rect x="9" y="2.5" width="3" height="8.5" rx="1" /><rect x="13.5" y="0" width="3" height="11" rx="1" />
        </svg>
        <svg width="16" height="11" viewBox="0 0 16 11" fill={c}>
          <path d="M8 2.2c2 0 3.8.8 5.1 2l1.1-1.2C13.6 1.4 10.9.4 8 .4S2.4 1.4.8 3l1.1 1.2C3.2 3 5 2.2 8 2.2z" />
          <path d="M8 5.4c1.1 0 2.1.4 2.9 1.2l1.1-1.2C10.9 4.2 9.5 3.6 8 3.6s-2.9.6-4 1.8l1.1 1.2C5.9 5.8 6.9 5.4 8 5.4z" />
          <circle cx="8" cy="9" r="1.6" />
        </svg>
        <div className="flex items-center gap-0.5">
          <div style={{ width: 22, height: 11, border: `1.5px solid ${c}`, borderRadius: 3, opacity: 0.9, display: "flex", alignItems: "center", padding: 1.5 }}>
            <div style={{ background: c, width: "80%", height: "100%", borderRadius: 1 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* Blue rounded header used on inner screens */
export function ScreenHeader({ title, subtitle, right, onBack }) {
  const navigate = useNavigate();
  return (
    <div className="gd-header-grad px-5 pb-6 pt-2" style={{ borderBottomLeftRadius: 28, borderBottomRightRadius: 28 }}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          {onBack && (
            <button onClick={() => (onBack === true ? navigate(-1) : onBack())} className="gd-tap grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white">
              <ChevronLeftIcon />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-extrabold text-white">{title}</h1>
            {subtitle && <p className="mt-0.5 text-sm text-white/70">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
    </div>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

/* Bottom navigation with center SOS */
export function BottomNav() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const items = [
    { icon: Home, label: "Home", to: "/home" },
    { icon: ClipboardList, label: "Tasks", to: "/explore" },
    { icon: null, label: "SOS", to: "/safety" },
    { icon: Wallet, label: "Earnings", to: "/earnings" },
    { icon: User, label: "Me", to: "/profile" },
  ];
  return (
    <div className="relative flex-shrink-0" style={{ height: 78 }}>
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-around bg-white px-4 pb-3 pt-2" style={{ boxShadow: "0 -8px 30px rgba(31,42,82,0.10)", borderTopLeftRadius: 26, borderTopRightRadius: 26, height: 78 }}>
        {items.map((it) => {
          if (it.label === "SOS") {
            return (
              <button key="sos" onClick={() => navigate("/safety")} className="gd-tap gd-sos-pulse -mt-8 grid h-16 w-16 place-items-center rounded-full text-white"
                style={{ background: "radial-gradient(circle at 35% 30%, #3a4ea8, #0e163d)", border: "4px solid #cfd6f5", boxShadow: "0 10px 24px rgba(14,22,61,.45)" }}>
                <div className="flex flex-col items-center">
                  <ShieldAlert size={22} />
                  <span className="mt-0.5 text-[9px] font-extrabold tracking-wide">SOS</span>
                </div>
              </button>
            );
          }
          const active = pathname === it.to;
          const Icon = it.icon;
          return (
            <button key={it.label} onClick={() => navigate(it.to)} className="gd-tap flex w-14 flex-col items-center gap-1">
              <Icon size={22} color={active ? "#2f47a8" : "#a3abc9"} strokeWidth={active ? 2.6 : 2} />
              <span className="text-[10px] font-bold" style={{ color: active ? "#2f47a8" : "#a3abc9" }}>{it.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
